<template>
  <div class="asset-history-container">
    <div class="card">
      <div class="card-header">
        <h5>
          <i class="bi bi-clock-history" />
          {{ $t('components.assetDetails.historyTitle') }}
        </h5>
      </div>
      <div class="card-body">
        <div class="asset-history-filters">
          <div class="d-flex flex-row flex-wrap gap-2 filter-controls">
            <date-picker
              v-model="startDate"
              class="flex-grow"
              :placeholder="$t('components.datepicker.startPlaceholder')"
              :type="'datetime'"
              :range="false"
              :start-time="{ hours: 0, minutes: 0 }"
              :time-picker-inline="true"
              @emitDate="emitStartDate"
            />
            <date-picker
              v-model="endDate"
              class="flex-grow"
              :placeholder="$t('components.datepicker.endPlaceholder')"
              :type="'datetime'"
              :range="false"
              :start-time="{ hours: 0, minutes: 0 }"
              :time-picker-inline="true"
              @emitDate="emitEndDate"
            />
            <button
              v-if="!isSearching"
              class="btn btn-success btn-search"
              @click="searchHistory"
            >
              {{ $t('components.search') }}
            </button>
            <button
              v-else
              class="btn btn-success btn-search btn-search-disabled"
              :title="$t('components.history.tooltip')"
              data-bs-toggle="tooltip"
              disabled
            >
              {{ $t('components.search') }}
            </button>
          </div>
          <div class="quick-range-buttons">
            <span class="quick-range-label">{{ $t('components.assetDetails.quickRangeLabel') }}</span>
            <button
              v-for="preset in rangePresets"
              :key="preset.key"
              type="button"
              class="btn btn-light btn-sm"
              :class="{ active: activePreset === preset.key }"
              @click="applyPreset(preset.key)"
            >
              {{ $t(preset.label) }}
            </button>
          </div>
          <div class="errors">
            <p
              v-if="filterError"
              class="error-msg"
            >
              {{ filterError }}
            </p>
          </div>
        </div>

        <div
          v-if="loading"
          class="text-center py-5"
        >
          <div
            class="spinner-border text-primary"
            role="status"
          >
            <span class="visually-hidden">{{ $t('components.assetDetails.loading') }}</span>
          </div>
        </div>

        <div
          v-else-if="error"
          class="alert alert-danger"
        >
          <i class="bi bi-exclamation-triangle" />
          {{ error }}
        </div>

        <div
          v-else-if="chartHasData"
          class="history-chart-wrapper"
        >
          <template v-if="!isChartFullscreen">
            <div
              class="chart-container"
              :class="{ 'dark-mode': isDarkMode }"
            >
              <div class="chart-buttons-floating">
                <button
                  class="btn btn-custom"
                  type="button"
                  @click="openChartFullscreen"
                  title="Pełny ekran"
                >
                  <i class="bi bi-arrows-fullscreen" />
                </button>
                <button
                  class="btn btn-custom"
                  type="button"
                  @click="resetChartZoom"
                  title="Reset zoom"
                >
                  <i class="bi bi-arrow-clockwise" />
                </button>
                <button
                  class="btn btn-custom chart-action-export"
                  type="button"
                  @click="exportToPng"
                  title="Eksport PNG"
                >
                  <i class="bi bi-filetype-png" />
                </button>
                <button
                  class="btn btn-custom chart-action-export"
                  type="button"
                  @click="exportToJpg"
                  title="Eksport JPG"
                >
                  <i class="bi bi-filetype-jpg" />
                </button>
                <button
                  class="btn btn-custom chart-action-export"
                  type="button"
                  @click="exportCsv"
                  title="Eksport CSV"
                >
                  <i class="bi bi-filetype-csv" />
                </button>
              </div>
              <LineChart
                ref="historyChart"
                :key="chartRenderKey"
                :height="550"
                :chart-id="chartId"
                :chart-data="chartData"
                :options="chartOptions"
                :plugins="chartPlugins"
              />
            </div>
          </template>
          <div class="series-table-wrapper">
            <DxDataGrid
              class="series-grid"
              :data-source="seriesStats"
              :show-borders="false"
              :row-alternation-enabled="true"
              :column-auto-width="true"
              :column-hiding-enabled="true"
              :hover-state-enabled="false"
            >
              <DxColumn
                data-field="visible"
                :caption="$t('components.assetDetails.seriesTable.visible')"
                cell-template="visibility-cell"
                :allow-sorting="false"
                :allow-filtering="false"
                alignment="center"
              />
              <DxColumn
                data-field="label"
                :caption="$t('components.assetDetails.seriesTable.series')"
                alignment="center"
              />
              <DxColumn
                data-field="min"
                :caption="$t('components.assetDetails.seriesTable.min')"
                cell-template="min-chip"
                alignment="center"
              />
              <DxColumn
                data-field="avg"
                :caption="$t('components.assetDetails.seriesTable.avg')"
                cell-template="avg-chip"
                alignment="center"
              />
              <DxColumn
                data-field="max"
                :caption="$t('components.assetDetails.seriesTable.max')"
                cell-template="max-chip"
                alignment="center"
              />
              <template #min-chip="{ data }">
                <div class="text-center">
                  <span class="badge rounded-pill bg-primary fw-semibold">
                    {{ data.data.min }}
                  </span>
                </div>
              </template>
              <template #avg-chip="{ data }">
                <div class="text-center">
                  <span class="badge rounded-pill bg-info text-white fw-semibold">
                    {{ data.data.avg }}
                  </span>
                </div>
              </template>
              <template #max-chip="{ data }">
                <div class="text-center">
                  <span class="badge rounded-pill bg-danger fw-semibold">
                    {{ data.data.max }}
                  </span>
                </div>
              </template>
              <template #visibility-cell="{ data }">
                <div class="visibility-cell">
                  <div class="form-check form-switch m-0">
                    <input
                      v-model="data.data.visible"
                      class="form-check-input"
                      type="checkbox"
                      role="switch"
                      @change="toggleSeriesVisibility(data.data)"
                    >
                  </div>
                </div>
              </template>
            </DxDataGrid>
          </div>
        </div>

        <div
          v-else
          class="text-center text-muted py-5"
        >
          <i class="bi bi-bar-chart" />
          <p>{{ $t('components.assetDetails.noHistory') }}</p>
        </div>
      </div>
    </div>
  </div>

  <teleport to="body">
    <div
      v-if="isChartFullscreen"
      class="chart-fullscreen-overlay"
      :class="{ 'dark-mode': isDarkMode }"
      @click.self="closeChartFullscreen"
    >
      <div
        class="chart-container chart-container-fullscreen"
        :class="{ 'dark-mode': isDarkMode }"
      >
        <div class="chart-buttons-floating">
          <button
            class="btn btn-custom"
            type="button"
            @click="closeChartFullscreen"
            title="Zamknij"
          >
            <i class="bi bi-x-lg" />
          </button>
          <button
            class="btn btn-custom"
            type="button"
            @click="resetChartZoom"
            title="Reset zoom"
          >
            <i class="bi bi-arrow-clockwise" />
          </button>
          <button
            class="btn btn-custom chart-action-export"
            type="button"
            @click="exportToPng"
            title="Eksport PNG"
          >
            <i class="bi bi-filetype-png" />
          </button>
          <button
            class="btn btn-custom chart-action-export"
            type="button"
            @click="exportToJpg"
            title="Eksport JPG"
          >
            <i class="bi bi-filetype-jpg" />
          </button>
          <button
            class="btn btn-custom chart-action-export"
            type="button"
            @click="exportCsv"
            title="Eksport CSV"
          >
            <i class="bi bi-filetype-csv" />
          </button>
        </div>
        <LineChart
          ref="historyChart"
          :key="chartRenderKey"
          :height="fullscreenChartHeight"
          :chart-id="chartId"
          :chart-data="chartData"
          :options="chartOptions"
          :plugins="chartPlugins"
        />
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import dayjs from 'dayjs'
import { LineChart } from 'vue-chart-3'
import { Chart, ChartData, ChartOptions, registerables } from 'chart.js'
import 'chartjs-adapter-moment'
import zoomPlugin from 'chartjs-plugin-zoom'

import DatePicker from '@/components/extras/DatePicker.vue'
import { DxDataGrid, DxColumn } from 'devextreme-vue/data-grid'
import { getAssetGroupHistoryRange } from '@/crud/asset-group'
import { resetZoom, exportToImg } from '@/utils/chart'
import { generateCsv, getHumanDate } from '@/utils/tools'
import {
  buildSeriesRecord,
  SERIES_KEYS,
  SERIES_META,
  type ChartSeriesRecord,
  type SeriesKey
} from '@/utils/asset-history-series'

Chart.register(...registerables)
Chart.register(zoomPlugin)

const RANGE_PRESETS = [
  { key: 'today', label: 'components.assetDetails.rangePresets.today' },
  { key: 'yesterday', label: 'components.assetDetails.rangePresets.yesterday' },
  { key: 'last24h', label: 'components.assetDetails.rangePresets.last24h' },
  { key: 'last7days', label: 'components.assetDetails.rangePresets.last7days' }
] as const

type RangePresetKey = typeof RANGE_PRESETS[number]['key']

export default defineComponent({
  name: 'AssetGroupHistory',
  components: {
    DatePicker,
    LineChart,
    DxDataGrid,
    DxColumn
  },
  props: {
    assetId: {
      type: [String, Number] as PropType<string | number>,
      required: true
    }
  },
  data() {
    const defaultEnd = dayjs()
    return {
      loading: true,
      error: null as string | null,
      filterError: '',
      startDate: defaultEnd.subtract(1, 'day').format('YYYY-MM-DD HH:mm'),
      endDate: defaultEnd.format('YYYY-MM-DD HH:mm'),
      isSearching: false,
      chartSeries: buildSeriesRecord({}, (key) => key) as ChartSeriesRecord,
      seriesVisibility: {
        temperature: true,
        humidity: true,
        powerSupply: false,
        batteryVoltage: false,
        batteryStatus: false,
        xAxis: true,
        yAxis: true,
        zAxis: true,
        magnitude: true,
        magnitudeNoGravity: true
      } as Record<SeriesKey, boolean>,
      isDarkMode: false,
      themeObserver: null as MutationObserver | null,
      isChartFullscreen: false,
      fullscreenChartHeight: 720,
      bodyOverflowBeforeFullscreen: '' as string,
      activePreset: null as RangePresetKey | null
    }
  },
  computed: {
    chartId(): string {
      return `asset-group-history-chart-${this.assetId}`
    },
    chartRenderKey(): string {
      return `${this.isDarkMode ? 'dark' : 'light'}-${this.isChartFullscreen ? 'fullscreen' : 'inline'}`
    },
    chartHasData(): boolean {
      return SERIES_KEYS.some((key) => this.chartSeries[key].points.length)
    },
    rangePresets() {
      return RANGE_PRESETS
    },
    chartData(): ChartData<'line'> {
      const datasets: Array<any> = []

      Object.entries(SERIES_META).forEach(([key, meta]) => {
        const seriesKey = key as SeriesKey
        const series = this.chartSeries?.[seriesKey]
        if (series && series.points && series.points.length) {
          datasets.push({
            data: series.points,
            yAxisID: meta.axisId,
            label: this.$t(meta.labelKey),
            borderColor: meta.color,
            backgroundColor: meta.color,
            borderWidth: 2,
            tension: 0.1,
            spanGaps: true,
            hidden: !this.seriesVisibility[seriesKey],
            customKey: seriesKey
          })
        }
      })

      return { datasets }
    },
    chartOptions(): ChartOptions<'line'> {
      const vm = this
      const axisDisplay = this.getAxisDisplayMap()
      const axes: Record<string, any> = {
        x: {
          type: 'time',
          time: {
            tooltipFormat: 'DD/MM HH:mm',
            displayFormats: {
              minute: 'DD/MM HH:mm',
              hour: 'DD/MM HH:mm',
              day: 'DD/MM'
            }
          },
          ticks: {
            color: this.isDarkMode ? 'rgba(255,255,255,0.7)' : undefined
          },
          grid: {
            color: this.isDarkMode ? 'rgba(255,255,255,0.05)' : undefined
          }
        }
      }

      Object.entries(SERIES_META).forEach(([key, meta]) => {
        if (axes[meta.axisId]) return

        const seriesKey = key as SeriesKey
        const isBatteryAxis = meta.axisId === 'batteryStatus'
        const isHumidityAxis = meta.axisId === 'humidity'
        const isBatteryVoltageAxis = meta.axisId === 'batteryVoltage'
        const axisTitle = meta.axisId === 'axis'
          ? this.$t('components.assetDetails.axisTitleDevice') as string
          : this.$t(meta.labelKey) as string

        axes[meta.axisId] = {
          type: 'linear',
          position: meta.axisPosition,
          display: axisDisplay[meta.axisId] ?? this.shouldDisplayAxis(seriesKey),
          min: isBatteryAxis ? -0.1 : (isHumidityAxis || isBatteryVoltageAxis ? 0 : undefined),
          max: isBatteryAxis ? 1.1 : (isHumidityAxis ? 100 : undefined),
          reverse: isBatteryAxis ? true : false,
          offset: meta.offset ?? false,
          title: {
            display: true,
            text: axisTitle,
            color: meta.color,
            font: { weight: 'bold' }
          },
          ticks: {
            color: meta.color,
            font: { weight: 'bold' },
            stepSize: isBatteryAxis ? 1 : (isHumidityAxis ? 10 : undefined),
            callback: (value: string | number) => {
              const numeric = typeof value === 'string' ? parseFloat(value) : value
              if (!Number.isFinite(numeric)) {
                return ''
              }
              if (isBatteryAxis) {
                if (numeric === 0) return vm.$t('common.ok') as string
                if (numeric === 1) return vm.$t('common.bad') as string
                return ''
              }
              return meta.tickFormatter ? meta.tickFormatter(numeric) : numeric
            }
          },
          grid: {
            drawOnChartArea: meta.axisId === 'temperature' || meta.axisId === 'powerSupply',
            color: vm.isDarkMode ? `${meta.color.replace('0.85', '0.2')}` : undefined
          }
        }
      })

      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        scales: axes,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              color: this.isDarkMode ? 'rgba(255, 255, 255, 0.9)' : undefined,
              font: this.isDarkMode ? { size: 14, weight: 'bold' } : undefined
            },
            onClick: (_event, legendItem, legend) => {
              const datasetIndex = legendItem.datasetIndex
              if (typeof datasetIndex !== 'number') return

              const dataset = legend.chart.data.datasets[datasetIndex] as any
              const seriesKey = dataset?.customKey as SeriesKey | undefined
              if (!seriesKey) return

              const nextVisibility = !this.seriesVisibility[seriesKey]
              this.onSeriesVisibilityToggle(seriesKey, nextVisibility, legend.chart)
            }
          },
          title: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const dataset = context.dataset as any
                const seriesKey = dataset?.customKey as SeriesKey | undefined
                const label = context.dataset?.label ?? ''
                const value = context.parsed.y
                const formattedValue = this.formatSeriesValue(seriesKey, value)
                const unit = seriesKey ? SERIES_META[seriesKey]?.unit || '' : ''
                return `${label}: ${formattedValue}${unit ? ' ' + unit : ''}`
              }
            }
          },
          zoom: {
            pan: {
              enabled: true,
              mode: 'x'
            },
            zoom: {
              wheel: { enabled: true },
              pinch: { enabled: true },
              mode: 'x'
            }
          }
        },
        elements: {
          point: {
            radius: 0,
            hitRadius: 5
          }
        },
        layout: {
          padding: {
            left: 20,
            right: 20,
            top: 20,
            bottom: 10
          }
        }
      }
    },
    chartPlugins(): any[] {
      const bgColor = this.isDarkMode ? '#1e1e2d' : '#f5f8fa'
      return [
        {
          id: 'assetHistoryBackground',
          beforeDraw: (chart: any) => {
            const ctx = chart.ctx
            ctx.save()
            ctx.fillStyle = bgColor
            ctx.fillRect(0, 0, chart.width, chart.height)
            ctx.restore()
          }
        }
      ]
    },
    seriesStats(): Array<any> {
      const excludedFromTable: SeriesKey[] = ['powerSupply', 'batteryStatus']
      return Object.entries(SERIES_META)
        .map(([key, meta]) => ({ key: key as SeriesKey, meta }))
        .filter(({ key }) => !excludedFromTable.includes(key))
        .filter(({ key }) => this.hasSeriesData(key))
        .map(({ key, meta }) => {
          const series = this.chartSeries[key]
          const numericValues = series.points.map(point => point.y)
          const min = numericValues.length ? Math.min(...numericValues) : null
          const max = numericValues.length ? Math.max(...numericValues) : null
          const avg = numericValues.length ? numericValues.reduce((sum, val) => sum + val, 0) / numericValues.length : null

          return {
            key,
            label: this.$t(meta.labelKey) as string,
            min: this.formatStatValue(key, min),
            avg: this.formatStatValue(key, avg),
            max: this.formatStatValue(key, max),
            visible: this.seriesVisibility[key]
          }
        })
    }
  },
  mounted() {
    this.checkDarkMode()
    this.observeTheme()
    this.fetchHistory()
  },
  beforeUnmount() {
    if (this.themeObserver) {
      this.themeObserver.disconnect()
    }
    this.closeChartFullscreen()
  },
  methods: {
    emitStartDate(value: string | string[]) {
      if (Array.isArray(value)) {
        this.startDate = dayjs(value[0]).format('YYYY-MM-DD HH:mm')
      } else {
        this.startDate = dayjs(value).format('YYYY-MM-DD HH:mm')
      }
    },
    emitEndDate(value: string | string[]) {
      if (Array.isArray(value)) {
        this.endDate = dayjs(value[0]).format('YYYY-MM-DD HH:mm')
      } else {
        this.endDate = dayjs(value).format('YYYY-MM-DD HH:mm')
      }
    },
    observeTheme() {
      this.themeObserver = new MutationObserver(() => {
        this.checkDarkMode()
      })
      this.themeObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      })
    },
    checkDarkMode() {
      this.isDarkMode = document.body.classList.contains('dark-theme')
    },
    async searchHistory() {
      if (this.isSearching) return
      this.fetchHistory(true)
    },
    applyPreset(key: RangePresetKey) {
      this.activePreset = key
      const now = dayjs()

      switch (key) {
        case 'today': {
          const start = dayjs().startOf('day')
          const end = dayjs().endOf('day')
          this.startDate = start.format('YYYY-MM-DD HH:mm')
          this.endDate = end.format('YYYY-MM-DD HH:mm')
          break
        }
        case 'yesterday': {
          const start = dayjs().subtract(1, 'day').startOf('day')
          const end = dayjs().subtract(1, 'day').endOf('day')
          this.startDate = start.format('YYYY-MM-DD HH:mm')
          this.endDate = end.format('YYYY-MM-DD HH:mm')
          break
        }
        case 'last24h': {
          const end = now
          const start = now.subtract(24, 'hours')
          this.startDate = start.format('YYYY-MM-DD HH:mm')
          this.endDate = end.format('YYYY-MM-DD HH:mm')
          break
        }
        case 'last7days': {
          const end = dayjs().endOf('day')
          const start = dayjs().subtract(6, 'day').startOf('day')
          this.startDate = start.format('YYYY-MM-DD HH:mm')
          this.endDate = end.format('YYYY-MM-DD HH:mm')
          break
        }
        default:
          this.activePreset = null
      }
    },
    async fetchHistory(isSearch = false) {
      if (isSearch) {
        this.isSearching = true
      }
      this.loading = true
      this.error = null

      try {
        const response = await getAssetGroupHistoryRange(
          this.assetId,
          this.getIsoDate(this.startDate),
          this.getIsoDate(this.endDate)
        )

        this.chartSeries = buildSeriesRecord(
          response.data?.series,
          (key) => this.$t(key) as string
        )
        this.$nextTick(() => {
          this.syncChartVisibility()
        })
      } catch (err: any) {
        console.error('Error loading asset history', err)
        this.error = this.$t('components.assetDetails.errorLoading') as string
      } finally {
        this.loading = false
        this.isSearching = false
      }
    },
    getIsoDate(date: string): string {
      const parsed = dayjs(date)
      if (!parsed.isValid()) {
        return dayjs().toISOString()
      }
      return parsed.toISOString()
    },
    hasSeriesData(key: SeriesKey): boolean {
      return this.chartSeries[key].points.length > 0
    },
    formatSeriesValue(key: SeriesKey | undefined, value: number | string | null): string {
      if (value === null) return ''

      if (key === 'batteryStatus') {
        const numeric = typeof value === 'number' ? value : parseFloat(String(value))
        if (Number.isNaN(numeric)) return String(value)
        return numeric === 0 ? this.$t('common.ok') as string : this.$t('common.bad') as string
      }

      if (key === 'powerSupply' || key === 'batteryVoltage') {
        const numeric = typeof value === 'number' ? value : parseFloat(String(value))
        if (Number.isNaN(numeric)) return String(value)
        return numeric.toFixed(2)
      }
      if (typeof value === 'number') return value.toString()
      const numeric = parseFloat(String(value))
      return Number.isNaN(numeric) ? String(value) : numeric.toString()
    },
    formatStatValue(key: SeriesKey, value: number | null): string {
      if (value === null || Number.isNaN(value)) {
        return '—'
      }

      if (key === 'batteryStatus') {
        return '—'
      }

      const precision = this.getSeriesPrecision(key)
      const unit = SERIES_META[key]?.unit || ''
      const formatted = precision !== null ? value.toFixed(precision) : value.toString()
      return unit ? `${formatted} ${unit}` : formatted
    },
    getSeriesPrecision(key: SeriesKey): number | null {
      switch (key) {
        case 'temperature':
          return 1
        case 'humidity':
          return 1
        case 'powerSupply':
        case 'batteryVoltage':
          return 2
        case 'batteryStatus':
          return 0
        case 'xAxis':
        case 'yAxis':
        case 'zAxis':
        case 'magnitude':
        case 'magnitudeNoGravity':
          return 2
        default:
          return null
      }
    },
    getAxisDisplayMap(): Record<string, boolean> {
      const display: Record<string, boolean> = {}

      Object.entries(SERIES_META).forEach(([key, meta]) => {
        const seriesKey = key as SeriesKey
        display[meta.axisId] = display[meta.axisId] || this.shouldDisplayAxis(seriesKey)
      })

      return display
    },
    shouldDisplayAxis(seriesKey: SeriesKey): boolean {
      const series = this.chartSeries[seriesKey]
      return series.points.length > 0 && this.seriesVisibility[seriesKey]
    },
    onSeriesVisibilityToggle(seriesKey: SeriesKey, isVisible: boolean, chart: any) {
      this.seriesVisibility[seriesKey] = isVisible
      chart?.update()
    },
    toggleSeriesVisibility(series: any) {
      this.seriesVisibility[series.key] = series.visible
    },
    resetChartZoom() {
      resetZoom(this.chartId)
    },
    openChartFullscreen() {
      this.isChartFullscreen = true
      this.bodyOverflowBeforeFullscreen = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      this.updateFullscreenChartHeight()
      window.addEventListener('resize', this.updateFullscreenChartHeight)
    },
    closeChartFullscreen() {
      this.isChartFullscreen = false
      document.body.style.overflow = this.bodyOverflowBeforeFullscreen || ''
      window.removeEventListener('resize', this.updateFullscreenChartHeight)
    },
    updateFullscreenChartHeight() {
      const padding = 160
      this.fullscreenChartHeight = Math.max(320, window.innerHeight - padding)
    },
    syncChartVisibility() {
      Object.keys(this.seriesVisibility).forEach((key) => {
        this.seriesVisibility[key as SeriesKey] = this.hasSeriesData(key as SeriesKey)
      })
    },
    exportToPng() {
      const filename = `asset_history_${this.assetId}_${getHumanDate(Date.now() / 1000)}`
      exportToImg('image/png', this.chartId, filename)
    },
    exportToJpg() {
      const filename = `asset_history_${this.assetId}_${getHumanDate(Date.now() / 1000)}`
      exportToImg('image/jpeg', this.chartId, filename)
    },
    exportCsv() {
      const exportRows = this.chartSeriesToRows()
      const filename = `asset_history_${this.assetId}_${getHumanDate(Date.now() / 1000)}`
      generateCsv(exportRows, filename, this.$t, this.$i18n.locale)
    },
    chartSeriesToRows() {
      const rows: any[] = []

      Object.entries(this.chartSeries).forEach(([key, series]) => {
        if (!series.points.length) return
        series.points.forEach((point) => {
          rows.push({
            device_timestamp: Math.floor(point.x / 1000),
            series: key,
            param_name: series.paramName,
            label: series.label,
            value: point.y,
            unit: series.unit
          })
        })
      })

      return rows
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/asset-history-view.scss';
</style>
